// _shared/hooks/useCompetitors.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { competitorsService } from '../../_services/competitorsService';

export const useCompetitors = (slug, isAdmin = false) => {
  // Estados principales
  const [competitors, setCompetitors] = useState([]);
  const [filteredCompetitors, setFilteredCompetitors] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [formFields, setFormFields] = useState([]);
  
  // Estados de UI
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingColumns, setIsSavingColumns] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Estados de edición
  const [editingRow, setEditingRow] = useState(null);
  const [editedData, setEditedData] = useState({});
  
  // Estados de confirmación
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [competitorToDelete, setCompetitorToDelete] = useState(null);
  
  // Estados de privacidad
  const [publicListEnabled, setPublicListEnabled] = useState(true);
  const [isListOcultaPublic, setIsListOcultaPublic] = useState(false);
  
  // Estados de paginación
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Estados de modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({});

  /**
   * Mostrar mensaje de éxito
   */
  const showSuccess = useCallback((msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  }, []);

  /**
   * Aplicar configuración de columnas
   */
 // _shared/hooks/useCompetitors.js

const applyColumnConfig = useCallback((allCols, visibleCols, availableColumns) => {
  const cols = allCols.length > 0 ? allCols : (visibleCols.length > 0 ? visibleCols : availableColumns);
  setAllColumns(cols);
  setSelectedColumns(prev => prev.length > 0 ? prev : cols);
  
  let visibles;
  
  if (isAdmin) {
    // Admin: mostrar solo las columnas marcadas como visibles
    visibles = cols.filter(col =>
      Array.isArray(visibleCols) &&
      visibleCols.some(v => v.toLowerCase() === col.toLowerCase())
    );
    // Fallback: si no hay config, mostrar todas
    if (visibles.length === 0 && (!Array.isArray(visibleCols) || visibleCols.length === 0)) {
      visibles = cols;
    }
  } else {
    // ✅ PÚBLICO: Usar columnas visibles configuradas por el admin
    // Con fallback a columnas por defecto si no hay config
    if (Array.isArray(visibleCols) && visibleCols.length > 0) {
      visibles = visibleCols;
    } else {
      // Fallback: columnas por defecto para vista pública
      const defaultPublicColumns = ['dorsal', 'nombre', 'apellido', 'categoria', 'club'];
      visibles = cols.filter(col => 
        defaultPublicColumns.some(def => col.toLowerCase().includes(def.toLowerCase()))
      );
      // Si no encuentra ninguna, mostrar todas excepto id/slug
      if (visibles.length === 0) {
        visibles = cols.filter(col => !['id', 'slug'].includes(col.toLowerCase()));
      }
    }
  }
  
  setVisibleColumns(visibles);
  
  const formColumns = allCols.length > 0 ? allCols : cols;
  setFormFields(
    formColumns.map(col => ({
      name: col,
      label: col,
      type: 'text'
    }))
  );
}, [isAdmin]);

  /**
   * Cargar datos
   */
  const fetchData = useCallback(async () => {
    if (!slug) return;
    try {
      setIsLoading(true);
      setError(null);
      let visibleCols = [];
      let allCols = [];
      let currentPublicListEnabled = publicListEnabled; // 🟢 variable local
  
      // 1. Obtener configuración de columnas
      try {
        const visibleResponse = await competitorsService.getVisibleColumns(slug);
        visibleCols = visibleResponse.visibleColumns || [];
        allCols = visibleResponse.allColumns || [];
  
        if (visibleResponse.publicListEnabled !== undefined) {
          const enabled = visibleResponse.publicListEnabled === 1 || visibleResponse.publicListEnabled === true;
          currentPublicListEnabled = enabled;  // 🟢 sincronizar variable local
          setPublicListEnabled(enabled);
          setIsListOcultaPublic(!enabled);
        }
      } catch (e) {
        console.error("Error al cargar columnas visibles:", e);
      }
  
      // 2. Obtener competidores
      const competitorsResponse = await competitorsService.getCompetitors(slug);
  
      if (competitorsResponse?.listOculta) {
        setIsListOcultaPublic(true);
        setPublicListEnabled(false);
      } else {
        setIsListOcultaPublic(!currentPublicListEnabled);  // 🟢 usar variable local
      }
  
      // ✅ Procesar datos de competidores (sin cambios)
      const competitorsData = Array.isArray(competitorsResponse)
        ? competitorsResponse
        : (competitorsResponse?.data || []);
  
      if (competitorsData.length > 0) {
        const sortedCompetitors = [...competitorsData].sort((a, b) => b.id - a.id);
        setCompetitors(sortedCompetitors);
        setFilteredCompetitors(sortedCompetitors);
        const availableColumns = Object.keys(competitorsData[0])
          .filter(column => column !== 'id' && column !== 'slug');
        applyColumnConfig(allCols, visibleCols, availableColumns);
      } else {
        setCompetitors([]);
        setFilteredCompetitors([]);
        if (allCols.length > 0) {
          applyColumnConfig(allCols, visibleCols, []);
        } else {
          setAllColumns([]);
          setVisibleColumns([]);
          setFormFields([]);
        }
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      setError("Error al cargar los datos");
    } finally {
      setIsLoading(false);
    }
  }, [slug, applyColumnConfig]);

  // Cargar datos al montar
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtrar por búsqueda
  useEffect(() => {
    if (!searchTerm) {
      setFilteredCompetitors(competitors);
      setCurrentPage(1);
      return;
    }

    const filtered = competitors.filter(competitor =>
      Object.values(competitor).some(value =>
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setFilteredCompetitors(filtered);
    setCurrentPage(1);
  }, [searchTerm, competitors]);

  /**
   * Guardar columnas visibles
   */
  const handleSaveColumns = async () => {
    try {
      if (!Array.isArray(visibleColumns)) {
        throw new Error('Datos de columnas no válidos');
      }

      setIsSavingColumns(true);
      await competitorsService.saveVisibleColumns(slug, visibleColumns, publicListEnabled);
      showSuccess('Configuración guardada correctamente');
    } catch (error) {
      console.error('Error:', error);
      setError(error.response?.data?.message || 'Error al guardar configuración');
    } finally {
      setIsSavingColumns(false);
    }
  };

  const handleTogglePublicList = async (newValue) => {
    // ✅ Guardar el valor exacto (true/false)
    setPublicListEnabled(newValue); // Actualiza el estado local primero
    
    try {
      setIsSavingColumns(true);
      
      // ✅ Enviar "publicListEnabled" como lo espera el backend
      await competitorsService.saveVisibleColumns(
        slug, 
        visibleColumns, 
        newValue // ← true o false, NO 1 o 0
      );
      
      showSuccess(newValue ? 'Lista pública activada correctamente' : 'Lista pública ocultada correctamente');
    } catch (error) {
      console.error('Error al guardar visibilidad:', error);
      setError(error.response?.data?.message || 'Error al guardar configuración');
      // Si falla, revertir el cambio visual
      setPublicListEnabled(!newValue);
    } finally {
      setIsSavingColumns(false);
    }
  };

  /**
   * Agregar competidor
   */
  const handleAddCompetitor = async () => {
    setIsAdding(true);
    try {
      const camposActivos = formFields.map(field => ({
        nombre: field.name,
        valor: newCompetitor[field.name] || ''
      }));

      await competitorsService.addCompetitor(slug, {
        paqueteSeleccionado: newCompetitor.paquete || 'Manual',
        camposActivos: camposActivos
      });

      setShowAddModal(false);
      setNewCompetitor({});
      showSuccess('Corredor agregado exitosamente');
      
      // Recargar datos
      await fetchData();
    } catch (error) {
      console.error('Error al agregar corredor:', error);
      setError(error.response?.data?.message || 'Error al agregar corredor');
    } finally {
      setIsAdding(false);
    }
  };


  /**
 * Agregar competidor con campos ya estructurados desde el formConfig
 */
const handleAddCompetitorConCampos = async (camposActivos) => {
  setIsAdding(true);
  try {
    await competitorsService.addCompetitor(slug, {
      paqueteSeleccionado: 'Manual',
      camposActivos: camposActivos,
    });
    setShowAddModal(false);
    showSuccess('Corredor agregado exitosamente');
    await fetchData();
    return true;
  } catch (error) {
    console.error('Error al agregar corredor:', error);
    setError(error.response?.data?.message || 'Error al agregar corredor');
    return false;
  } finally {
    setIsAdding(false);
  }
};

  /**
   * Editar competidor
   */
  const handleEdit = (competitor) => {
    setEditingRow(competitor.id);
    setEditedData({ ...competitor });
  };

  /**
   * Guardar edición
   */
  const handleSaveEdit = async () => {
    if (!editingRow) return;

    try {
      setIsSaving(true);
      setError(null);

      await competitorsService.editCompetitor(slug, editingRow, editedData);

      // Actualizar lista local
      const updatedCompetitors = competitors.map(comp =>
        comp.id === editingRow ? { ...comp, ...editedData } : comp
      );
      setCompetitors(updatedCompetitors);
      setFilteredCompetitors(updatedCompetitors);
      setEditingRow(null);
      setEditedData({});
      showSuccess('Cambios guardados correctamente');
    } catch (error) {
      console.error('Error al guardar:', error);
      setError(error.response?.data?.message || error.message);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Eliminar competidor
   */
  const handleDelete = async (id) => {
    if (!id || !slug) {
      setError("ID o slug indefinido para eliminar");
      return;
    }

    try {
      setIsDeleting(true);
      await competitorsService.deleteCompetitor(slug, id);
      
      const updatedCompetitors = competitors.filter((competitor) => competitor.id !== id);
      setCompetitors(updatedCompetitors);
      setFilteredCompetitors(updatedCompetitors);
      showSuccess('Competidor eliminado correctamente');
    } catch (error) {
      setError(error.message);
      console.error("Error en la solicitud de eliminación:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Descargar Excel
   */
  const handleDownload = async () => {
    try {
      const columns = selectedColumns.length ? selectedColumns : allColumns;
      const blob = await competitorsService.downloadExcel(slug, columns);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `competidores_${slug}.xlsx`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      setError('Error al descargar el archivo');
    }
  };

  // Paginación
  const totalPages = Math.ceil(filteredCompetitors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCompetitors = filteredCompetitors.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Confirmación
  const confirmActionHandler = (action, competitorId = null) => {
    setConfirmAction(action);
    setCompetitorToDelete(competitorId);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    if (confirmAction === "save") {
      await handleSaveEdit();
    } else if (confirmAction === "delete" && competitorToDelete) {
      await handleDelete(competitorToDelete);
    }
  };

  // Handlers para cambios
  const handleVisibleColumnChange = (column) => {
    setVisibleColumns(prev =>
      prev.includes(column)
        ? prev.filter(col => col !== column)
        : [...prev, column]
    );
  };

  const handleEditChange = (column, value) => {
    setEditedData(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditedData({});
  };

// ==========================================
// 1. Detectar el nombre de la columna de categoría en la BD
// ==========================================
const categoryColumnName = useMemo(() => {
  if (!allColumns || allColumns.length === 0) return null;
  const found = allColumns.find(col =>
    col.toLowerCase().includes('categor') || col.toLowerCase().includes('category')
  );
  return found || null;
}, [allColumns]);

// ==========================================
// 2. Detectar la columna de dorsal (siempre es 'dorsal' del sistema de kits)
// ==========================================
const dorsalColumnName = useMemo(() => {
  if (!allColumns || allColumns.length === 0) return 'dorsal';
  const found = allColumns.find(col =>
    col.toLowerCase() === 'dorsal' ||
    col.toLowerCase() === 'bib' ||
    col.toLowerCase() === 'numero'
  );
  return found || 'dorsal';
}, [allColumns]);

// ==========================================
// 3. Helper para normalizar (agrupar "master a" y "Master A" juntos)
// ==========================================
const normalizeCategory = useCallback((value) => {
  if (!value) return 'Sin Categoría';
  return value.toString().trim().toLowerCase().replace(/\s+/g, ' ');
}, []);

// ==========================================
// 4. Helper para capitalizar (mostrar bonito: "Master A")
// ==========================================
const capitalizeCategory = useCallback((value) => {
  if (!value || value === 'Sin Categoría') return 'Sin Categoría';
  return value.toString().trim().split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}, []);

// ==========================================
// 5. 🟢 Obtener categoría del competidor
//    Prioridad: columna de categoría real → paquete → null
// ==========================================
const getCompetitorCategory = useCallback((comp) => {
  // Prioridad 1: columna de categoría real del formulario
  if (categoryColumnName && comp[categoryColumnName]) {
    return comp[categoryColumnName].toString().trim();
  }
  // Prioridad 2: fallback al paquete (10K, 5K, etc.)
  if (comp.paquete) return comp.paquete.toString().trim();
  if (comp.package) return comp.package.toString().trim();
  return null;
}, [categoryColumnName]);

// ==========================================
// 6. 🟢 Obtener dorsal del competidor
// ==========================================
const getCompetitorDorsal = useCallback((comp) => {
  return comp[dorsalColumnName] || null;
}, [dorsalColumnName]);

  return {
    // Estados
    competitors,
    filteredCompetitors,
    allColumns,
    visibleColumns,
    selectedColumns,
    formFields,
    isLoading,
    isSaving,
    isSavingColumns,
    isDeleting,
    isAdding,
    error,
    successMessage,
    editingRow,
    editedData,
    showConfirm,
    confirmAction,
    competitorToDelete,
    publicListEnabled,
    isListOcultaPublic,
    searchTerm,
    itemsPerPage,
    currentPage,
    totalPages,
    startIndex,
    paginatedCompetitors,
    showAddModal,
    newCompetitor,
    isAdmin,
    categoryColumnName,
    normalizeCategory,
    capitalizeCategory,
    getCompetitorCategory,
    getCompetitorDorsal,

    // Setters
    setPublicListEnabled,
    setSearchTerm,
    setItemsPerPage,
    setCurrentPage,
    setShowAddModal,
    setNewCompetitor,
    setSelectedColumns,
    setError,
    setShowConfirm,

    // Handlers
    handleSaveColumns,
    handleTogglePublicList,
    handleAddCompetitor,
    handleAddCompetitorConCampos,
    handleEdit,
    handleSaveEdit,
    handleDelete,
    handleDownload,
    handleVisibleColumnChange,
    handleEditChange,
    cancelEdit,
    confirmActionHandler,
    handleConfirm,
    goToPage,
    showSuccess,
    fetchData,
    refreshCompetitors: fetchData,
  };
};